// MAC (change) orders and ticket charge matching per Omni Rick Round Sections 3.2, 3.3, 4 (Phase 7).

import type {
  Billing,
  BillingLink,
  CatalogProduct,
  Cents,
  ContractTerm,
  CustomerBilling,
  Dealer,
  DealerModel,
  Iso,
  OneBillMapping,
  Quote,
  QuoteType,
} from "./index.js";
import type { AutotaskSync, CatalogProductV2 } from "./catalog-v2.js";
import { productKey } from "./catalog-v2.js";
import { exactName, scoreNameSimilarity } from "./customers-v2.js";

// ---------- Quote Type & Labels ----------

export const QUOTE_TYPE_MAC_LABEL = "MAC" as const;

export const QUOTE_TYPE_LABELS: Record<QuoteType, string> = {
  new: "New",
  change: "MAC",
  renewal: "Renewal",
};

export function formatQuoteType(type: QuoteType): string {
  return QUOTE_TYPE_LABELS[type] ?? type;
}

// ---------- Contract Term Rules ----------

export function isCoterminousAllowed(quoteType: QuoteType): boolean {
  return quoteType === "change";
}

export function validateContractTerm(
  term: ContractTerm,
  quoteType: QuoteType,
): { ok: true } | { ok: false; reason: string } {
  if (term === "coterminous" && !isCoterminousAllowed(quoteType)) {
    return {
      ok: false,
      reason: "Coterminous term is only available for MAC (change) quotes.",
    };
  }
  return { ok: true };
}

// ---------- Quote Source (Autotask Ticket) ----------

export interface CustomerTicketSummary {
  id: number;
  ticketNumber: string;
  title: string;
  status: number;
  statusName: string | null;
  queueId: number | null;
  queueName: string | null;
  issueTypeId: number | null;
  issueTypeName: string | null;
  createDate: string | null;
  chargeCount: number;
}

export interface AutotaskTicketSource {
  kind: "autotask_ticket";
  ticketId: number;
  ticketNumber: string;
}

export type QuoteSource = AutotaskTicketSource;

// ---------- Order Kind ----------

export type OrderKind = "new" | "mac";

export function orderKind(order: { kind?: OrderKind | null }): OrderKind {
  return order.kind ?? "new";
}

// ---------- Mark Sold Request ----------

export interface MarkSoldRequest {
  expectedUpdatedAt: Iso;
}

export function validateMarkSoldRequest(
  input: unknown,
): { ok: true; data: MarkSoldRequest } | { ok: false; error: string } {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, error: "expectedUpdatedAt is required." };
  }
  const obj = input as Record<string, unknown>;
  if (typeof obj.expectedUpdatedAt !== "string" || !obj.expectedUpdatedAt.trim()) {
    return { ok: false, error: "expectedUpdatedAt is required." };
  }
  return { ok: true, data: { expectedUpdatedAt: obj.expectedUpdatedAt.trim() } };
}

// ---------- isBillable (Section 3.2) ----------

export type BillableRefusalCode =
  | "no_billing"
  | "inactive"
  | "missing_autotask_link"
  | "autotask_not_in_sync"
  | "autotask_active_differs"
  | "missing_onebill_link"
  | "missing_wholesale_cost";

export type IsBillableResult =
  | { ok: true }
  | { ok: false; code: BillableRefusalCode; reason: string };

export interface BillableCheckProduct {
  billing?: Billing | null;
  active?: boolean;
  autotaskActive?: boolean;
  autotask?: { kind: "service" | "product"; id: number } | null;
  autotaskSync?: AutotaskSync | null;
  billingLink?: BillingLink | null;
  onebill?: OneBillMapping | null;
  wholesaleCost?: Cents | null;
  enabled?: boolean;
}

export interface IsBillableOptions {
  model?: DealerModel;
  wholesale?: boolean;
}

/**
 * Checks whether a catalog product is billable.
 * Plan Section 3.2:
 * "A matched line is usable only if it is billable: billing set, active,
 * Autotask link in sync, OneBill link linked. The dealer enabled flag
 * doesn't apply to ops MAC orders. A line that isn't billable shows the reason."
 */
export function isBillable(
  product: BillableCheckProduct,
  options?: IsBillableOptions,
): IsBillableResult {
  // 1. Billing set
  if (!product.billing) {
    return {
      ok: false,
      code: "no_billing",
      reason: "Billing period is not set",
    };
  }

  // 2. Active in Autotask / Omni
  if (product.active === false || product.autotaskActive === false) {
    return {
      ok: false,
      code: "inactive",
      reason: "Product is inactive",
    };
  }

  // 3. Autotask link present
  if (!product.autotask) {
    return {
      ok: false,
      code: "missing_autotask_link",
      reason: "Missing Autotask link",
    };
  }

  // 4. Autotask link in sync
  if (product.autotaskSync) {
    const { state, differences } = product.autotaskSync;
    if (state === "creating" || state === "push_failed" || state === "missing_in_autotask") {
      return {
        ok: false,
        code: "autotask_not_in_sync",
        reason: `Autotask link is not in sync (${state})`,
      };
    }
    if (state === "changed_in_autotask") {
      const activeDiff = (differences ?? []).some((d) => d.field === "active");
      if (activeDiff) {
        return {
          ok: false,
          code: "autotask_active_differs",
          reason: "Autotask active status differs from Omni",
        };
      }
    }
  }

  // 5. OneBill link linked
  if (product.billingLink?.state !== "linked" || !product.onebill) {
    return {
      ok: false,
      code: "missing_onebill_link",
      reason: "Product is not linked to OneBill",
    };
  }

  // 6. Optional wholesale cost check if explicit wholesale model requested
  const isWholesale = options?.model === "wholesale" || options?.wholesale === true;
  if (isWholesale && (product.wholesaleCost === null || product.wholesaleCost === undefined)) {
    return {
      ok: false,
      code: "missing_wholesale_cost",
      reason: "Product has no wholesale cost set",
    };
  }

  // Dealer enabled flag intentionally ignored for ops MAC orders per Section 3.2
  return { ok: true };
}

// ---------- Ticket Charges & MAC Lines (Section 3.2) ----------

export interface TicketChargeInput {
  id: number;
  ticketID?: number;
  name: string;
  description?: string | null;
  chargeType?: number;
  productID?: number | null;
  contractServiceID?: number | null;
  unitQuantity?: number | null;
  unitPrice?: number | null;
  unitCost?: number | null;
  datePurchased?: string | null;
  isBillableToCompany?: boolean | null;
}

export interface MacLineCandidate {
  state: "matched" | "unmatched";
  chargeId: number;
  chargeName: string;
  name: string;
  productId: string | null;
  product?: CatalogProduct | CatalogProductV2 | null;
  why: "product_id" | "exact_name" | "product_key" | null;
  qty: number;
  quantityValid: boolean;
  quantityError?: string;
  unitPrice: Cents | null;
  chargeUnitPrice: number;
  chargeUnitPriceCents: Cents;
  billable?: IsBillableResult;
  suggestions: Array<{ productId: string; name: string; score: number }>;
  reason?: string;
  flags?: string[];
}

export interface MacLinesMatchOptions {
  suggestionMinScore?: number;
  billableOptions?: IsBillableOptions;
}

/**
 * Derives MAC line candidates from Autotask ticket charges per Section 3.2:
 * - A charge with a productID matches the catalog product whose autotask is {kind: "product", id: productID}.
 * - A charge without one goes through exact name, then product key. Certain only on exactly one candidate.
 * - Otherwise unmatched and named (e.g. "Shipping"), with up to 3 suggestions.
 * - Matched line is checked with isBillable.
 * - Quantity comes from unitQuantity (positive integer, otherwise flagged).
 * - Price comes from catalog list price, with the charge's unitPrice shown beside it.
 */
export function macLinesFromCharges(
  charges: TicketChargeInput[],
  catalog: Array<CatalogProduct | CatalogProductV2>,
  options?: MacLinesMatchOptions,
): MacLineCandidate[] {
  return charges.map((charge) => {
    // 1. Quantity validation
    const rawQty = charge.unitQuantity;
    const quantityValid = typeof rawQty === "number" && Number.isInteger(rawQty) && rawQty > 0;
    const qty = quantityValid ? rawQty : typeof rawQty === "number" && rawQty > 0 ? Math.floor(rawQty) : 1;
    const quantityError = quantityValid ? undefined : "Quantity must be a positive integer";
    const flags: string[] = [];
    if (!quantityValid) {
      flags.push("invalid_quantity");
    }

    // 2. Charge unit price (in dollars and cents)
    const chargeUnitPrice = typeof charge.unitPrice === "number" ? charge.unitPrice : 0;
    const chargeUnitPriceCents = Math.round(chargeUnitPrice * 100);

    // 3. Match against catalog
    let matchedProduct: CatalogProduct | CatalogProductV2 | null = null;
    let why: "product_id" | "exact_name" | "product_key" | null = null;

    // Tier 1: productID match
    if (charge.productID !== null && charge.productID !== undefined && charge.productID > 0) {
      const byProductId = catalog.filter(
        (p) => p.autotask && p.autotask.kind === "product" && p.autotask.id === charge.productID,
      );
      if (byProductId.length === 1) {
        matchedProduct = byProductId[0];
        why = "product_id";
      }
    }

    // Tier 2: Exact name match (if no productID)
    if (!matchedProduct && (!charge.productID || charge.productID <= 0)) {
      const normName = exactName(charge.name);
      if (normName) {
        const byExactName = catalog.filter((p) => exactName(p.name) === normName);
        if (byExactName.length === 1) {
          matchedProduct = byExactName[0];
          why = "exact_name";
        }
      }
    }

    // Tier 3: Product key match (if no exact name match and no productID)
    if (!matchedProduct && (!charge.productID || charge.productID <= 0)) {
      const key = productKey(charge.name);
      if (key) {
        const byKey = catalog.filter((p) => productKey(p.name) === key);
        if (byKey.length === 1) {
          matchedProduct = byKey[0];
          why = "product_key";
        }
      }
    }

    // 4. Return matched candidate
    if (matchedProduct) {
      const billable = isBillable(matchedProduct, options?.billableOptions);
      if (!billable.ok) {
        flags.push("not_billable");
      }
      return {
        state: "matched",
        chargeId: charge.id,
        chargeName: charge.name,
        name: matchedProduct.name,
        productId: matchedProduct.id,
        product: matchedProduct,
        why,
        qty,
        quantityValid,
        quantityError,
        unitPrice: matchedProduct.listPrice,
        chargeUnitPrice,
        chargeUnitPriceCents,
        billable,
        suggestions: [],
        flags: flags.length > 0 ? flags : undefined,
      };
    }

    // 5. Return unmatched candidate with scored suggestions
    const minScore = options?.suggestionMinScore ?? 0.5;
    const scored: Array<{ productId: string; name: string; score: number }> = [];
    for (const p of catalog) {
      const score = scoreNameSimilarity(charge.name, p.name);
      if (score >= minScore) {
        scored.push({
          productId: p.id,
          name: p.name,
          score: Number(score.toFixed(3)),
        });
      }
    }
    scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
    const suggestions = scored.slice(0, 3);
    flags.push("unmatched");

    return {
      state: "unmatched",
      chargeId: charge.id,
      chargeName: charge.name,
      name: charge.name,
      productId: null,
      product: null,
      why: null,
      qty,
      quantityValid,
      quantityError,
      unitPrice: null,
      chargeUnitPrice,
      chargeUnitPriceCents,
      suggestions,
      reason: `Charge "${charge.name}" is not in the catalog`,
      flags: flags.length > 0 ? flags : undefined,
    };
  });
}

// ---------- canMarkSold (Section 4 line 461) ----------

export function canMarkSold(
  quote: Pick<Quote, "type" | "source" | "lines">,
  catalog: Array<CatalogProduct | CatalogProductV2>,
  customerBilling?: CustomerBilling | null,
): { ok: true } | { ok: false; reason: string } {
  if (quote.type !== "change") {
    return { ok: false, reason: "Only MAC (change) quotes can be marked sold." };
  }
  if (!quote.source || quote.source.kind !== "autotask_ticket") {
    return { ok: false, reason: "Quote must have a source Autotask ticket." };
  }
  if (!quote.lines || quote.lines.length === 0) {
    return { ok: false, reason: "Add at least one product." };
  }
  if (customerBilling && customerBilling.state !== "linked") {
    return { ok: false, reason: "Link this customer's OneBill account first." };
  }
  for (const line of quote.lines) {
    const prod = catalog.find((p) => p.id === line.productId);
    if (!prod) {
      return { ok: false, reason: `Product "${line.name}" not found in catalog.` };
    }
    const billable = isBillable(prod);
    if (!billable.ok) {
      return { ok: false, reason: `Line "${line.name}" is not billable: ${billable.reason}` };
    }
  }
  return { ok: true };
}

// ---------- MAC PDF Variant Options ----------

export const MAC_PDF_TITLE = "MAC order" as const;

export interface MacPdfOptions {
  title?: string;
  omitSignaturePage?: boolean;
}

export interface QuotePdfOptions {
  quote: Quote;
  dealer: Dealer;
  repName: string;
  taxNote: string;
  variant?: "quote" | "mac";
  title?: string;
  omitSignaturePage?: boolean;
}

export function resolvePdfTitle(quoteType: QuoteType, customTitle?: string): string {
  if (customTitle) return customTitle;
  if (quoteType === "change") return MAC_PDF_TITLE;
  return "Quote";
}

export function shouldOmitSignaturePage(quoteType: QuoteType, override?: boolean): boolean {
  if (override !== undefined) return override;
  return quoteType === "change";
}

// ---------- Test Fakes & Helpers ----------

export function fakeTicketCharge(overrides: Partial<TicketChargeInput> = {}): TicketChargeInput {
  return {
    id: 1001,
    ticketID: 5001,
    name: "Yealink T54W IP Phone",
    chargeType: 1,
    productID: 101,
    unitQuantity: 1,
    unitPrice: 199.99,
    unitCost: 120.0,
    isBillableToCompany: true,
    ...overrides,
  };
}

export function fakeCatalogProductForMac(
  overrides: Partial<CatalogProductV2> = {},
): CatalogProductV2 {
  return {
    id: "p-poly-vvx450",
    autotask: { kind: "product", id: 101 },
    autotaskSync: {
      state: "in_sync",
      period: "one_time",
      checkedAt: "2026-10-17T12:00:00.000Z",
      pushedAt: null,
      error: null,
      differences: [],
    },
    code: "POLY-450",
    name: "Polycom VVX 450",
    description: "12-line IP Desk Phone",
    category: "Hardware",
    billing: "one_time",
    unit: "device",
    listPrice: 19900,
    unitCost: 12000,
    active: true,
    floorPrice: 15900,
    wholesaleCost: 14000,
    enabled: true,
    onebill: { productCode: "POLY450", pricePlanId: "PLAN-ONETIME" },
    billingLink: {
      state: "linked",
      reviewedBy: "ops@phoneware.us",
      reviewedAt: "2026-10-17T12:00:00.000Z",
    },
    createdBy: "ops@phoneware.us",
    createdAt: "2026-10-17T12:00:00.000Z",
    updatedAt: "2026-10-17T12:00:00.000Z",
    ...overrides,
  };
}
